import { create } from "zustand";
import { persist } from "zustand/middleware";
import { jwtStillLive } from "@/utils/jwt";

export interface TenderBadgePreference {
  show_needs_pricing: boolean;
  show_needs_approval: boolean;
  show_needs_offer: boolean;
  show_needs_result: boolean;
}

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: "director" | "coordinator" | "accountant" | "employee" | "contractor" | "hr";
  phone_number: string;
  profile_picture_url: string | null;
  can_approve_orders: boolean;
  can_view_tenders: boolean;
  can_calculate_pricing: boolean;
  can_approve_pricing: boolean;
  can_issue_offers: boolean;
  can_view_pricing_catalog: boolean;
  can_manage_contractors: boolean;
  /** CEO-granted flag: may create agreement groups and move framework agreements between them. */
  can_manage_agreement_groups?: boolean;
  /** Effective right to manage agreement groups: CEO / technical director by role OR the explicit flag. */
  has_manage_agreement_groups_permission?: boolean;
  /** CEO-granted flag: may confirm / reject title reports from companies leading ZE projects. */
  can_confirm_led_titles?: boolean;
  /** Effective right to decide title reports (badge „N tyt.”, portfolio filter, Potwierdź/Odrzuć):
   *  CEO / technical director by role OR the explicit flag. */
  has_led_title_confirmation_permission?: boolean;
  /** Explicit CEO grant: may create przedsięwzięcia (projects) despite not being a technical director. */
  can_create_projects?: boolean;
  /** Effective right to create przedsięwzięcia: CEO / technical director by role OR the explicit flag. */
  has_project_creation_permission?: boolean;
  is_finance_panel: boolean;
  is_ceo: boolean;
  can_use_aron_autopilot?: boolean;
  can_use_ai_workshop?: boolean;
  /** ARON may read the RAW source code for this person (grep_code/read_code). CEO always. */
  can_read_source_code?: boolean;
  is_uz: boolean;
  employment_fraction_numerator: number;
  employment_fraction_denominator: number;
  is_vat_payer: boolean;
  vat_rate: "zw" | "0" | "5" | "8" | "23" | null;
  contract_type: "uop" | "uz" | "b2b";
  work_type: "on_site" | "remote";
  // Employment start (YYYY-MM-DD) derived by the backend from salary costs
  // (primary cost's effective_from, else the oldest); null = no costs =
  // considered already working. Months before it require no timesheet.
  // Optional so older persisted sessions (pre-feature) still type-check.
  employment_start_date?: string | null;
  // Own "Moje karty godzin" / "Moje faktury B2B" — kept after a contract switch while
  // there is paperwork under the previous type (backend contract_timeline). Only on
  // the logged-in user's own payload; optional for older persisted sessions.
  contract_views?: { timesheets: boolean; b2b_invoices: boolean } | null;
  contract_expiry_info: { company_name: string; effective_to: string; days_to_expiry: number }[];
  is_developer: boolean;
  is_fleet_manager: boolean;
  is_supply_manager: boolean;
  has_small_account_access: boolean;
  can_self_allocate: boolean;
  has_friday_access: boolean;
  has_contacts_access: boolean;
  /** Contractor-only scope of Kontakty: branch ids whose staff the account sees. */
  contacts_branches?: string[];
  /** Contractor-only: Kontakty also lists staff without a branch. */
  contacts_include_no_branch?: boolean;
  has_multisport_access: boolean;
  /** Explicit grant to the browser-extensions panel (Wtyczki). */
  has_browser_extensions_access?: boolean;
  /** Effective access to Wtyczki: developer OR the explicit flag. */
  has_browser_extensions_permission?: boolean;
  /** CEO-granted admission of a CONTRACTOR account to GG. */
  has_chat_access?: boolean;
  /**
   * Effective admission to GG. True for every GK employee regardless of the flag —
   * never gate the UI on `has_chat_access` alone or the whole company loses GG.
   */
  has_chat_permission?: boolean;
  /** CEO-granted admission of a CONTRACTOR account to the TwoDrive file cloud. */
  has_two_drive_access?: boolean;
  /**
   * Effective admission to TwoDrive. True for every GK employee regardless of the
   * flag — never gate the UI on `has_two_drive_access` alone or the whole company
   * loses the drive.
   */
  has_two_drive_permission?: boolean;
  /** CEO-granted access to the lead module (Pozyskiwanie). */
  has_lead_access?: boolean;
  /** Effective access to Pozyskiwanie: CEO OR the explicit flag. */
  has_lead_permission?: boolean;
  /** CEO-granted access to the eop@ mailbox (poczta ENERGA w DLC_01). */
  has_eop_mail_access?: boolean;
  /**
   * Effective access to the eop@ mailbox. Beware: true also for coordinators of
   * any ENERGA project, who get it WITHOUT the flag — never gate the UI on
   * `has_eop_mail_access` alone or half the intended audience loses the screen.
   */
  has_eop_mail_permission?: boolean;
  job_title: string;
  branch: { id: string; name: string; city?: string } | null;
  is_technical_director: boolean;
  /** Contractor accounts only — may this user see rates on their company's orders/agreements. */
  can_see_rates: boolean;
  can_view_all_timesheets_and_mileage: boolean;
  can_use_estimating: boolean;
  has_ze_advisor_access: boolean;
  has_ze_tender_analytics_access: boolean;
  /** Explicit CEO grant: may show inactive (terminated) employees in the work schedule. */
  can_view_inactive_in_schedule?: boolean;
  /** Effective right to show inactive employees in the schedule: CEO OR the explicit flag. */
  has_view_inactive_in_schedule_permission?: boolean;
  has_orderer_permission: boolean;
  /** Wejście do zamówień spoza roli: nadany dostęp do zamówienia albo bycie zamawiającym. */
  has_granted_order_access?: boolean;
  has_view_tenders_permission: boolean;
  has_calculate_pricing_permission: boolean;
  has_approve_pricing_permission: boolean;
  has_issue_offers_permission: boolean;
  has_view_pricing_catalog_permission: boolean;
  has_manage_contractors_permission: boolean;
  has_group_finance_access: boolean;
  has_any_finance_access: boolean;
  has_consortium_access: boolean;
  has_submit_paper_docs_permission: boolean;
  can_view_correspondence_register: boolean;
  has_correspondence_register_permission: boolean;
  // Read-only access to the full register (all directors) — no intake/dekretacja rights.
  // Optional so older persisted sessions (pre-feature) still type-check.
  has_correspondence_register_view_permission?: boolean;
  can_manage_doc_clusters: boolean;
  has_doc_cluster_permission: boolean;
  /** Opt-in (Ustawienia konta): powiadomienia i badge o zleceniach wydruku do realizacji.
   *  Optional so older persisted sessions (pre-feature) still type-check. */
  receives_print_order_notifications?: boolean;
  is_signatory: boolean;
  has_signatory_permission: boolean;
  // DLC_10 Obliczenia techniczne — the norm catalog (przewody/słupy).
  // Optional so older persisted sessions (pre-feature) still type-check.
  /** Explicit CEO grant: raw flag as stored on the user. */
  has_tech_calc_norms_access?: boolean;
  has_rfq_manage_access?: boolean;
  has_rfq_sealed_access?: boolean;
  has_rfq_view_access?: boolean;
  has_rfq_manage_permission?: boolean;
  has_rfq_sealed_permission?: boolean;
  has_rfq_view_permission?: boolean;
  can_access_rfq?: boolean;
  /** Contractor accounts only — may open "Prowadzone projekty" (apps.contractor_led). */
  can_access_led_projects?: boolean;
  /** Effective access (view + edit) to the norm catalog: CEO/developer OR the flag. */
  has_tech_calc_norms_permission?: boolean;
  // DLC modules this user may access ("dlc_01" Projekty ZE, "dlc_02" Obieg dokumentów).
  // CEO sees all; contractors none; others only modules granted in the CEO DLC panel.
  accessible_dlc_modules: string[];
  internal_cost_access_level: "aggregate" | "full" | null;
  contractor_company: string | null;
  contractor_company_name: string | null;
  /** Firma konta ma pozycję sprawdzenia dokumentacji projektowej w umowie ramowej (albo
      dawny typ „Projektant sprawdzający”, albo już klaster do podpisu) — menu dokumentacji. */
  contractor_company_is_design_reviewer?: boolean;
  // Per-user choice of which tender action-status badges show in the nav (Przetargi + ZE).
  // Optional so older persisted sessions (pre-feature) still render — treat missing as all-on.
  tender_badge_preference?: TenderBadgePreference;
  // Fee requests on projects the user does not coordinate. CEO + technical
  // directors have it by role; anyone else needs the CEO-granted flag.
  // Optional so older persisted sessions (pre-feature) still type-check.
  can_request_fees_any_project?: boolean;
  has_request_fees_any_project_permission?: boolean;
  // False only for contractor users whose orders all have
  // contractor_fee_requests_allowed=False and who have no fee-request history.
  // Optional so older persisted sessions (pre-feature) still type-check.
  can_request_official_fees?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  /**
   * Set on every wipe of the tokens, and read by the OTHER tabs through the storage
   * event. True = a person signed out and every tab must follow. False = one tab
   * decided its own credentials were dead, which says nothing about a tab holding a
   * newer, working session. See the storage listener at the bottom of this file.
   */
  deliberateLogout?: boolean;
  setAuth: (user: AuthUser, accessToken: string, refreshToken: string) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  updateUser: (user: AuthUser) => void;
  logout: (opts?: { deliberate?: boolean }) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      setAuth: (user, accessToken, refreshToken) =>
        set({ user, accessToken, refreshToken, deliberateLogout: false }),
      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken, deliberateLogout: false }),
      updateUser: (user) => set({ user }),
      logout: (opts) =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          deliberateLogout: opts?.deliberate === true,
        }),
    }),
    { name: "elg5-auth" }
  )
);

/**
 * Keep token state in sync across browser tabs.
 *
 * When another tab rotates the JWT pair (ROTATE_REFRESH_TOKENS=True) and writes to
 * localStorage, Zustand's in-memory state here becomes stale. Rehydrating on the
 * storage event ensures subsequent requests in this tab use the fresh token and
 * don't try to refresh with the already-retired one.
 *
 * The one thing we refuse to adopt is ANOTHER tab's automatic sign-out while this
 * tab's own access token is still live. Every tab shares this single storage key, so
 * a tab left open since yesterday — whose credentials went stale where it sat — used
 * to null the pair for the whole browser the moment it woke up and failed a refresh.
 * That killed sessions that had just been created: prod, 12.09.2026, login at
 * 16:33:51.937 and a forced return to /auth/login at 16:33:53.377, with no request
 * refused by the server in between. A person signing out on purpose still propagates
 * (deliberateLogout), because that is an instruction, not a stale tab's conclusion.
 */
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== "elg5-auth") return;

    let incoming: Partial<AuthState> | null = null;
    try {
      incoming = e.newValue ? JSON.parse(e.newValue)?.state ?? null : null;
    } catch {
      incoming = null;
    }

    const wipedByAnotherTab =
      incoming !== null && !incoming.accessToken && !incoming.refreshToken;
    const ours = useAuthStore.getState();

    if (wipedByAnotherTab && !incoming?.deliberateLogout && jwtStillLive(ours.accessToken)) {
      // Our credentials outlived theirs. Put them back so the tab that gave up can
      // pick the working session up again instead of bouncing to the login screen.
      try {
        localStorage.setItem(
          "elg5-auth",
          JSON.stringify({
            state: {
              user: ours.user,
              accessToken: ours.accessToken,
              refreshToken: ours.refreshToken,
              deliberateLogout: false,
            },
            version: 0,
          }),
        );
      } catch {
        // Storage unavailable — this tab keeps its in-memory session regardless.
      }
      return;
    }

    useAuthStore.persist.rehydrate();
  });
}
